// @flow

import cookie from 'react-cookies';
// import { Platform } from 'react-native';
// import DeviceInfo from 'react-native-device-info';
import { zipObject, toString, range, map } from 'lodash';
import {
  getJson,
  postJson,
  delJson,
  post,
  put,
  putJson,
  paramify,
  appendParamsToQueryString,
} from '../utilities/fetchUtils';

import {
  reorderCarousel,
  changeEnabledStatus,
  carouselsToHashMap,
  updateTimerPreferences,
} from './utils';

import type { ThunkAction } from '../types';
import type {
  ProfilePreferencesResponse,
  AlterCarouselType,
  CarouselPreference,
  TimerPreferencesPayload,
  TimerPreferenceType,
  AccountPreferencesPayload,
  PreferencesPayload,
} from './types';

import { checkPasscodeExists as fetchLegacyDevices } from '../profiles/actions';

export const fetchPreferences = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  const url = `${config.session.radish_url}v21/dol/profile_preferences`;

  dispatch({ type: 'REQUEST_ACCOUNT_PREFERENCES' });
  dispatch({ type: 'REQUEST_PROFILE_PREFERENCES' });
  dispatch({ type: 'REQUEST_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE' });
  dispatch({ type: 'REQUEST_TIMER_PREFERENCES' });
  dispatch({ type: 'REQUEST_USER_PROFILES' });

  return getJson({ url, state })
    .then((payload: PreferencesPayload) => {
      /* eslint-disable camelcase */
      const {
        timer,
        show_scores,
        receivers,
        profiles,
        pref_hide_sd_dups,
        parental_controls,
        current_receiver,
        guide_channel_sources,
        guide_channels_sort_ascending,
        carousels,
        other_hardware,
      } = payload;

      dispatch({
        type: 'RECEIVE_ACCOUNT_PREFERENCES',
        payload: { parental_controls, preferences: { pref_hide_sd_dups, current_receiver } },
      });

      dispatch({
        type: 'RECEIVE_PROFILE_PREFERENCES',
        payload: {
          carousels: zipObject<string, CarouselPreference>(
            map<number, string>(range(carousels.length), toString),
            carousels,
          ),
          guide_channel_sources,
          guide_channels_sort_ascending,
        },
      });

      dispatch({
        type: 'RECEIVE_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE',
        payload: { pref_gamefinder_show_scores: show_scores },
      });

      dispatch({ type: 'RECEIVE_TIMER_PREFERENCES', payload: { timer_properties: { ...timer } } });
      dispatch({ type: 'RECEIVE_USER_PROFILES', payload: { ...profiles } });

      /* list of receivers is stored in auth/state */
      dispatch({ type: 'RECEIVE_RECEIVERS_LIST', payload: { receivers, joeys: other_hardware } });
    })
    .catch(err => {
      console.warn(`REQUEST_PREFERENCES_ERROR - fail to fetch preferences: ${err}`);

      dispatch({ type: 'REQUEST_ACCOUNT_PREFERENCES_ERROR' });
      dispatch({ type: 'REQUEST_PROFILE_PREFERENCES_ERROR' });
      dispatch({ type: 'REQUEST_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE_ERROR' });
      dispatch({ type: 'REQUEST_TIMER_PREFERENCES_ERROR' });
      dispatch({ type: 'REQUEST_USER_PROFILES_ERROR' });
    });
};
export const fetchAccountPreferences = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) {
    return undefined;
  }

  dispatch({ type: 'REQUEST_ACCOUNT_PREFERENCES' });

  const data = { uuid };
  const url = `${config.session.radish_url}v20/dol/account_preferences`;

  return getJson({ url, state, data })
    .then((payload: AccountPreferencesPayload) => {
      const { receivers, other_hardware, ...restOfPayload } = payload;

      /* list of receivers is stored in auth/state */
      dispatch({ type: 'RECEIVE_RECEIVERS_LIST', payload: { receivers, joeys: other_hardware } });

      dispatch({ type: 'RECEIVE_ACCOUNT_PREFERENCES', payload: restOfPayload });
    })
    .catch(error => {
      console.warn('Error during fetching account preferences:', error);
      dispatch({ type: 'REQUEST_ACCOUNT_PREFERENCES_ERROR' });
    });
};

export const fetchProfilePreferences = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) {
    return undefined;
  }

  dispatch({ type: 'REQUEST_PROFILE_PREFERENCES' });

  const profileId = state.profiles.currentProfile.profileId || cookie.load('rs') || '';

  const data = {
    uuid,
    profile_id: profileId,
  };
  const url = `${config.session.radish_url}v20/dol/profile_preferences`;

  return getJson({ url, data, state })
    .then((payload: ProfilePreferencesResponse) => {
      dispatch({ type: 'RECEIVE_PROFILE_PREFERENCES', payload });
    })
    .catch(error => {
      console.warn('Error during fetching profile preferences:', error);
      dispatch({ type: 'REQUEST_PROFILE_PREFERENCES_ERROR' });
    });
};

export const fetchProfilePreferencesIfNotAlreadyFetched = (): ThunkAction => ({
  getState,
  dispatch,
}) => {
  const state = getState();
  const { preferences: profilePreferences } = state.preferences.profilePreferences;
  if (profilePreferences && Array.isArray(profilePreferences.carousels)) {
    return;
  }
  dispatch(fetchProfilePreferences());
};

export const rebootReceiver = (receiverId: string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return;
  }

  const headers = { 'Content-Type': 'application/json' };

  const data = JSON.stringify({ receiver_id: receiverId });
  const url = `${config.session.radish_url}v20/dol/users/reboot_receiver.json`;

  dispatch({ type: 'REQUEST_RECEIVER_REBOOT', payload: { receiverId } });

  putJson({ url, data, state, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_RECEIVER_REBOOT', payload: { receiverId } });
    })
    .catch(error => {
      console.warn('Error during requesting reboot of a receiver', error);
      dispatch({ type: 'REQUEST_RECEIVER_REBOOT_ERROR', payload: { receiverId } });
    });
};

export const setUserCarouselPreferences = (alterAction: AlterCarouselType): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return undefined;
  }

  const carousels = state.preferences.profilePreferences.preferences?.carousels;
  if (!carousels) {
    /* This likely won't happen if user is logged in */
    return undefined;
  }

  let updatedCarousels;

  switch (alterAction.type) {
    case 'reorder_carousel': {
      const { fromIndex, toIndex } = alterAction;
      updatedCarousels = reorderCarousel(carousels, fromIndex, toIndex);
      break;
    }
    case 'enable_disable_carousel': {
      const { carouselId, enabled } = alterAction;
      updatedCarousels = changeEnabledStatus(carousels, carouselId, enabled);
      break;
    }
    default:
      (alterAction.type: empty); /* eslint-disable-line */
      updatedCarousels = false;
      break;
  }

  if (updatedCarousels === false) {
    /* Nothing has changed - nothing to update */
    return undefined;
  }

  dispatch({
    type: 'REQUEST_UPDATE_CAROUSEL_PREFERENCES',
    payload: { carousels: updatedCarousels },
  });

  /* CAUTION: DE8210 - this enpoint (http://int-necho-docs.dishanywhere.com/user#Zeus_Carousel_Preferences-PUT-Set-users-carousel-preferences)
     accepts literally anything as a payload, and sends this payload back (in GET request).
     Be very careful when post data (expected (default) type of carousels described in CarouselPreferences typedef) */

  const data = JSON.stringify(carouselsToHashMap(updatedCarousels));
  const headers = { 'Content-Type': 'application/json' };

  const baseUrl = `${config.session.radish_url}v20/dol/users/custom_carousels`;

  const profileId = state.profiles.currentProfile.profileId || cookie.load('rs') || '';

  const url = appendParamsToQueryString(
    baseUrl,
    paramify({
      euid,
      profile_id: profileId,
    }),
  );

  return put({ state, url, headers, data })
    .then(() => {
      dispatch({ type: 'RECEIVE_UPDATE_CAROUSEL_PREFERENCES' });
    })
    .catch(error => {
      dispatch({ type: 'REQUEST_UPDATE_CAROUSEL_PREFERENCES_ERROR' });
      console.warn(
        `REQUEST_UPDATE_CAROUSEL_PREFERENCES_ERROR error during updating carousel preferences: ${error}`,
      );
    });
};

export const resetCarousel = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();

  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return undefined;
  }

  const headers = { 'Content-Type': 'application/json' };
  const url = `${config.session.radish_url}v20/dol/users/custom_carousels`;

  return delJson({ state, url, headers })
    .then((carousels: Array<CarouselPreference>) => {
      dispatch({ type: 'RECEIVE_RESET_CAROUSEL_PREFERENCES', payload: carousels });
    })
    .catch(error => {
      console.warn(
        `REQUEST_RESET_CAROUSEL_PREFERENCES_ERROR can't reset user carousels settings: ${error}`,
      );
      dispatch({ type: 'REQUEST_RESET_CAROUSEL_PREFERENCES_ERROR' });
    });
};

export const clearErrorMessage = () => ({ type: 'CLEAR_REMOVE_ERROR_MESSAGE' });

export const changeSdHdDupsPreference = (hideSdDups: boolean): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return undefined;
  }
  dispatch({ type: 'REQUEST_CHANGE_SD_HD_DUPS_PREFERENCE', payload: { hideSdDups } });

  const url = `${config.session.radish_url}v20/dol/users/update_hide_sd_dups.json`;
  const headers = { 'Content-Type': 'application/json' };

  const data = JSON.stringify({
    pref_hide_sd_dups: hideSdDups,
  });

  return postJson({ state, url, data, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_CHANGE_SD_HD_DUPS_PREFERENCE' });
    })
    .catch(err => {
      console.warn(`Error during changing guide SD/HD dups preferences: ${err}`);
      dispatch({ type: 'REQUEST_CHANGE_SD_HD_DUPS_PREFERENCE_ERROR' });
    });
};

export const fetchHideScoresOnScorePagePreference = (): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) {
    return undefined;
  }

  dispatch({ type: 'REQUEST_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE' });

  const url = `${config.session.radish_url}v20/dol/gamefinder/show_scores.json`;

  return getJson({ state, url })
    .then(resp => {
      dispatch({
        type: 'RECEIVE_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE',
        payload: resp,
      });
    })
    .catch(err => {
      console.warn(`Error during fetching "show scores on sport page" preference: ${err}`);
      dispatch({ type: 'REQUEST_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE_ERROR' });
    });
};

export const setHideScoresOnScorePage = (showScores: boolean): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) {
    return undefined;
  }
  dispatch({ type: 'REQUEST_SET_SHOW_SCORES_ON_SPORT_PAGE' });

  const headers = { 'Content-Type': 'application/json' };
  const url = `${config.session.radish_url}v20/dol/gamefinder/show_scores.json`;

  const data = JSON.stringify({ pref_gamefinder_show_scores: showScores });
  return post({ state, url, data, headers })
    .then(() => {
      dispatch(fetchHideScoresOnScorePagePreference());
      dispatch({ type: 'RECEIVE_SET_SHOW_SCORES_ON_SPORT_PAGE' });
    })
    .catch(err => {
      console.warn(`Error during changing "show scores on sport page" preference: ${err}`);
      dispatch({ type: 'REQUEST_SET_SHOW_SCORES_ON_SPORT_PAGE_ERROR' });
    });
};

export const setGuideSorting = (ascending: boolean): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) {
    return undefined;
  }

  const baseUrl = `${config.session.radish_url}v20/dol/profile_preferences`;
  const headers = { 'Content-Type': 'application/json' };

  const profileId = state.profiles.currentProfile.profileId || cookie.load('rs') || '';

  const url = appendParamsToQueryString(
    baseUrl,
    paramify({
      uuid,
      profile_id: profileId,
    }),
  );

  const data = JSON.stringify({
    guide_channels_sort_ascending: ascending,
  });

  dispatch({ type: 'REQUEST_CHANGE_GUIDE_SORTING', payload: { ascending } });
  return put({ state, url, data, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_CHANGE_GUIDE_SORTING' });
    })
    .catch(err => {
      console.warn(`Error during settings guide sorting preference: ${err}`);
      dispatch({ type: 'REQUEST_CHANGE_GUIDE_SORTING_ERROR' });
    });
};

export const fetchUserTimerPreferences = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return undefined;
  }

  const headers = { Accept: 'application/vnd.echostar.timer_preferences+json;version=1' };
  const url = `${config.session.radish_url}timer_preferences?euid=${euid}`;

  dispatch({ type: 'REQUEST_TIMER_PREFERENCES' });

  return getJson({ state, url, headers })
    .then((payload: TimerPreferencesPayload) => {
      dispatch({ type: 'RECEIVE_TIMER_PREFERENCES', payload });
    })
    .catch(error => {
      dispatch({ type: 'REQUEST_TIMER_PREFERENCES_ERROR' });
      console.warn(
        `REQUEST_TIMER_PREFERENCES_ERROR fail to fetch user timer preferences: ${error}`,
      );
    });
};

export const saveUserTimerPreferences = (
  subPreferenceType: TimerPreferenceType,
  value: string,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { euid } = {} },
    preferences: {
      timerPreferences: { preferences },
    },
  } = state;

  if (!euid || !preferences) {
    return undefined;
  }

  const { updatedPreferences, valueBeforeUpdate } = updateTimerPreferences(
    preferences,
    subPreferenceType,
    value,
  );

  dispatch({
    type: 'REQUEST_SAVE_TIMER_PREFERENCES',
    payload: { timerPreferences: updatedPreferences },
  });

  const data = JSON.stringify({ timer_properties: updatedPreferences });
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/vnd.echostar.timer_preferences+json;version=1',
  };
  const url = `${config.session.radish_url}timer_preferences?euid=${euid}&preferences=${paramify(
    data,
  )}`;

  return postJson({ url, state, headers })
    .then(() =>
      dispatch({ type: 'RECEIVE_SAVE_TIMER_PREFERENCES', payload: { subPreferenceType, value } }),
    )
    .catch((error: Error) => {
      dispatch({
        type: 'REQUEST_SAVE_TIMER_PREFERENCES_ERROR',
        payload: {
          subPreferenceType,
          valueBeforeUpdate,
        },
      });
      console.warn(
        `REQUEST_SAVE_TIMER_PREFERENCES_ERROR: Error posting account timer preferences: ${
          error.message
        }`,
      );
    });
};

export const fetchDynaDevices = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();

  const headers = { Accept: 'application/vnd.echostar.dyna_device+json;version=1' };
  const url = `${config.session.radish_url}dyna_device`;

  dispatch({ type: 'REQUEST_DYNA_DEVICES' });
  return getJson({ state, url, headers })
    .then(payload => {
      dispatch({ type: 'RECEIVE_DYNA_DEVICES', payload });
    })
    .catch(error => {
      dispatch({ type: 'REQUEST_DYNA_DEVICES_ERROR' });
      console.warn(`REQUEST_DYNA_DEVICES_ERROR fail to fetch user timer preferences: ${error}`);
    });
};

export const registerDynaDevice = (newNickname?: string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const { device } = state;

  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return undefined;
  }

  const baseUrl = `${config.session.radish_url}dyna_device`;
  const data = {
    nickname: newNickname || device.deviceName,
    device_id: device.deviceId,
    machine_os: device.deviceOs,
  };
  const url = appendParamsToQueryString(baseUrl, paramify(data));

  dispatch({ type: 'REQUEST_REGISTER_DYNA_DEVICE', payload: data });

  const headers = {
    'Content-Type': 'application/json',
    accept: 'application/vnd.echostar.dyna_device+json;version=1',
  };

  return putJson({ state, url, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_REGISTER_DYNA_DEVICE' });
    })
    .then(() => {
      dispatch(fetchDynaDevices());
    })
    .catch(err => {
      console.warn(`Error registering this device: ${err}`);
      dispatch({ type: 'REQUEST_REGISTER_DYNA_DEVICE_ERROR' });
    });
};

export const deleteDynaDevice = (deviceId: string, callback?: boolean => void): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return undefined;
  }

  const data = {
    deviceId,
  };

  dispatch({ type: 'REQUEST_DELETE_DYNA_DEVICE', payload: data });

  const url = `${config.session.radish_url}dyna_device?device_id=${deviceId}`;
  const headers = {
    'Content-Type': 'application/json',
    accept: 'application/vnd.echostar.dyna_device+json;version=1',
  };

  return delJson({ state, url, headers })
    .then(() => {
      if (callback) {
        callback(true);
      }
      dispatch({ type: 'RECEIVE_DELETE_DYNA_DEVICE', payload: data });
    })
    .then(() => {
      dispatch(fetchDynaDevices());
    })
    .catch(err => {
      console.warn(`Error deleteing this device: ${err}`);
      if (callback) {
        callback(false);
      }
      dispatch({
        type: 'REQUEST_DELETE_DYNA_DEVICE_ERROR',
        payload: {
          ...data,
          errorDescription: 'Error removing device',
        },
      });
    });
};

export const deleteLegacyDevice = (deviceId: string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return undefined;
  }

  const data = {
    deviceId,
  };

  dispatch({ type: 'REQUEST_DELETE_LEGACY_DEVICE', payload: data });

  const url = `${config.session.radish_url}v20/dol/users/device/${deviceId}`;
  const headers = {
    'Content-Type': 'application/json',
  };

  return delJson({ state, url, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_DELETE_LEGACY_DEVICE' });
    })
    .then(() => {
      dispatch(fetchLegacyDevices());
    })
    .catch(err => {
      console.warn(`Error deleteing this device: ${err}`);
      dispatch({
        type: 'REQUEST_DELETE_LEGACY_DEVICE_ERROR',
      });
    });
};

export const storeLegacyDeviceList = (devices: Array<Object>): ThunkAction => ({ dispatch }) => {
  dispatch({
    type: 'STORE_LEGACY_DEVICE_LIST',
    payload: devices,
  });
};
